import { useCallback } from 'react';
import { useGetMyPromptsQuery, promptApi } from '../../../store/services';
import { useAppSelector, useAppDispatch } from '../../../store/hooks';
import { selectMyFeedPrompts, selectMyFeedCache } from '../../../store/slices/prompts';
import { PromptDto } from '../../../components/types';

type UseMyPromptsFeedReturn = {
    prompts: PromptDto[];
    loading: boolean;
    hasMore: boolean;
    loadMore: () => Promise<void>;
};

/**
 * Хук для управления лентой промптов пользователя с автоподгрузкой
 * @param pageSize - Количество промптов на странице (по умолчанию 20)
 * @returns Объект с данными и методами управления
 */
export const useMyPromptsFeed = (pageSize: number = 20): UseMyPromptsFeedReturn => {
    const dispatch = useAppDispatch();

    // Получаем данные из Redux
    const prompts = useAppSelector(selectMyFeedPrompts);
    const cache = useAppSelector(selectMyFeedCache);

    // RTK Query только для выполнения запросов
    const { isFetching } = useGetMyPromptsQuery({ limit: pageSize });

    const loadMore = useCallback(async (): Promise<void> => {
        if (!cache?.nextCursor || !cache?.hasMore || isFetching) {
            return;
        }

        try {
            await dispatch(
                promptApi.endpoints.getMyPrompts.initiate(
                    { cursor: cache.nextCursor, limit: pageSize },
                    { subscribe: false, forceRefetch: true },
                ),
            ).unwrap();
        } catch (error) {
            console.error('Failed to load more prompts:', error);
        }
    }, [cache?.nextCursor, cache?.hasMore, isFetching, pageSize, dispatch]);

    return {
        prompts,
        loading: isFetching,
        hasMore: cache?.hasMore ?? true,
        loadMore,
    };
};
